import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardFooter,
  Divider,
  FormControl,
  FormLabel,
  IconButton,
  Image,
  Input,
  Stack,
  Text,
  Textarea,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import {
  AiOutlineDelete,
  AiOutlineArrowUp,
  AiOutlineArrowDown,
  FiPlusSquare,
  FiDownload,
} from 'react-icons/all';
import { useDispatch, useSelector } from 'react-redux';
import { setSite } from '../../../features/site/siteSlice';
import { getBackendBaseURL } from '../../../utils/utils';
import ImageUploader from '../../common/image-uploader';
import { updateSite } from '../../../api/siteApi';

function AboutService({ isEdit, prevClicked, nextClicked }) {
  const { user } = useSelector(state => state.user);
  const { site } = useSelector(state => state.site);
  const dispatch = useDispatch();
  const [aboutServiceDescription, setAboutServiceDescription] = useState('');
  const [shouldShowAddButton, setShouldShowAddButton] = useState(true);
  const [inProgress, setInProgress] = useState(false);
  const [displayNext, setDisplayNext] = useState(isEdit);
  const [actionStatus, setActionStatus] = useState('');
  const [services, setServices] = useState([
    {
      title: '',
      description: '',
      src: '',
      docSrc: '',
      id: Math.random().toString(36).substring(2, 9),
    },
  ]);
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const toast = useToast();

  useEffect(() => {
    if (isEdit) {
      setupComponent();
    }
    // eslint-disable-next-line
  }, []);

  const generateId = () => Math.random().toString(36).substring(2, 9);

  const setupComponent = () => {
    const maxServices = 3;
    setAboutServiceDescription(site?.aboutServices?.description ?? '');
    let initialServices = site?.aboutServices?.services ?? [
      { title: '', description: '', src: '', docSrc: '', id: generateId() },
    ];

    initialServices = initialServices.map(service => ({
      ...service,
      id: service.id || generateId(),
    }));

    setServices(initialServices);
    setShouldShowAddButton(initialServices.length < maxServices);
  };

  const handleChangeTitle = (i, event) => {
    const values = [...services];
    values[i].title = event.target.value;
    setServices(values);
  };

  const handleChangeDescription = (i, event) => {
    const values = [...services];
    values[i].description = event.target.value;
    setServices(values);
  };

  const handleChangePath = (i, imagePath) => {
    const values = [...services];
    values[i].src = imagePath;
    setServices(values);
  };

  const handleChangeDocPath = (i, docPath) => {
    const values = [...services];
    values[i].docSrc = docPath || '';
    setServices(values);
  };

  const handleRemoveDoc = i => {
    const values = [...services];
    values[i].docSrc = '';
    setServices(values);
  };

  const handleAdd = () => {
    const values = [...services];

    // Default limit since templates no longer specify max services.
    const maxServices = 4;

    if (values.length >= maxServices) {
      toast({
        title: 'Limit Reached',
        description: `Your selected template only supports up to ${maxServices} services.`,
        status: 'warning',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
      return;
    }

    if (values.length + 1 >= maxServices) {
      setShouldShowAddButton(false);
    }

    values.push({
      title: '',
      description: '',
      src: '',
      docSrc: '',
      id: generateId(),
    });
    setServices(values);
  };

  const handleRemove = i => {
    const values = [...services];
    if (values.length > 1) {
      setShouldShowAddButton(true);
    }
    values.splice(i, 1);
    setServices(values);
  };

  const getImageUrl = path => {
    let url = path;
    if (url && !url.startsWith('http')) {
      url = `${getBackendBaseURL()}/${url}`;
    }
    return url;
  };

  const handleMoveUp = i => {
    if (i === 0) return;
    const values = [...services];
    const temp = values[i];
    values[i] = values[i - 1];
    values[i - 1] = temp;
    setServices(values);
  };

  const handleMoveDown = i => {
    if (i === services.length - 1) return;
    const values = [...services];
    const temp = values[i];
    values[i] = values[i + 1];
    values[i + 1] = temp;
    setServices(values);
  };

  const addAboutServiceDetails = () => {
    if (aboutServiceDescription === '' || services.length < 1) {
      toast({
        title: 'Error',
        description: 'Please set the proper details',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
      return;
    }
    setInProgress(true);

    const payload = {
      aboutServiceDescription,
      services,
      id: site._id,
      creator: user._id,
      updateType: 'ABOUT_SERVICES',
    };

    updateSite(payload)
      .then(response => {
        setInProgress(false);
        if (response.site) {
          if (isEdit) {
            dispatch(setSite(response.site));
          }
          let message = isEdit
            ? 'Updated About Services details Successfully.'
            : 'Added About Services details, Please add next details.';
          toast({
            title: 'Success',
            description: message,
            status: 'success',
            duration: 5000,
            isClosable: true,
            position: 'top-right',
          });
          setActionStatus(`${message}`);
          setDisplayNext(true);
        } else {
          toast({
            title: 'Error',
            description: response.message,
            status: 'error',
            duration: 5000,
            isClosable: true,
            position: 'top-right',
          });
        }
      })
      .catch(error => {
        setInProgress(false);
        toast({
          title: 'Error',
          description: 'Something went wrong, please try again',
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        });
      });
  };

  return (
    <Card bg={useColorModeValue('white', 'gray.700')}>
      <CardBody>
        <Stack spacing="4" mt="2">
          <FormControl isRequired>
            <FormLabel>Add Description about your services</FormLabel>
            <Textarea
              placeholder="Enter the details about you"
              defaultValue={aboutServiceDescription}
              onChange={event => setAboutServiceDescription(event.target.value)}
            />
          </FormControl>
          <Divider />
          <Box display="flex">
            <Text>Services</Text>
            <Button
              ml={'auto'}
              leftIcon={<FiPlusSquare />}
              colorScheme="teal"
              size="sm"
              display={shouldShowAddButton ? 'block' : 'none'}
              onClick={handleAdd}
            >
              Add Service
            </Button>
          </Box>
          {services.map((field, idx) => {
            return (
              <Stack
                padding={'2'}
                as={Card}
                key={field.id}
                spacing={2}
                direction={{ base: 'column', lg: 'row' }}
              >
                <FormControl isRequired>
                  <FormLabel>Service Title</FormLabel>
                  <Input
                    type="text"
                    name="text"
                    placeholder="Enter Service Title"
                    value={field.title}
                    onChange={event => handleChangeTitle(idx, event)}
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Service description</FormLabel>
                  <Textarea
                    placeholder="Enter Service Description"
                    value={field.description}
                    onChange={event => handleChangeDescription(idx, event)}
                  />
                </FormControl>
                {field.src !== '' && isEdit ? (
                  <Box w="100%" mt={2}>
                    <Stack direction="column" spacing={4} mb={4}>
                      <Box flexShrink={0}>
                        <Image
                          src={getImageUrl(field.src)}
                          alt="Service Photo"
                          maxH="100px"
                          objectFit="contain"
                          borderRadius="md"
                          border="1px solid"
                          borderColor={borderColor}
                          p={2}
                        />
                      </Box>
                      <FormControl>
                        <ImageUploader
                          label={'Change Image'}
                          OnUpload={path => handleChangePath(idx, path)}
                        />
                      </FormControl>
                    </Stack>
                  </Box>
                ) : (
                  <FormControl isRequired>
                    <FormLabel>Select Image</FormLabel>
                    <ImageUploader
                      label={'Select Image'}
                      OnUpload={path => handleChangePath(idx, path)}
                    />
                  </FormControl>
                )}
                <FormControl>
                  <FormLabel>Document (Optional)</FormLabel>
                  {field.docSrc ? (
                    <Stack
                      direction="column"
                      align="left"
                      spacing={2}
                      wrap="wrap"
                    >
                      <Button
                        as="a"
                        href={getImageUrl(field.docSrc)}
                        target="_blank"
                        rel="noopener noreferrer"
                        download
                        size="sm"
                        colorScheme="teal"
                        variant="solid"
                        leftIcon={<FiDownload />}
                      >
                        Download Document
                      </Button>

                      <Button
                        size="sm"
                        colorScheme="red"
                        variant="outline"
                        onClick={() => handleRemoveDoc(idx)}
                        leftIcon={<AiOutlineDelete />}
                      >
                        Remove Doc
                      </Button>
                    </Stack>
                  ) : (
                    <ImageUploader
                      label={'Upload Document'}
                      accept=".pdf,.doc,.docx,.ppt,.pptx,.txt,.xls,.xlsx,application/pdf"
                      OnUpload={path => handleChangeDocPath(idx, path)}
                    />
                  )}
                </FormControl>
                <FormControl paddingTop={'8'} display={'flex'} gap={2}>
                  <ButtonGroup
                    size="md"
                    isAttached
                    variant="outline"
                    ml={'auto'}
                    mr={2}
                  >
                    <IconButton
                      aria-label="Move Up"
                      icon={<AiOutlineArrowUp />}
                      onClick={() => handleMoveUp(idx)}
                      isDisabled={idx === 0}
                    />
                    <IconButton
                      aria-label="Move Down"
                      icon={<AiOutlineArrowDown />}
                      onClick={() => handleMoveDown(idx)}
                      isDisabled={idx === services.length - 1}
                    />
                  </ButtonGroup>
                  <Button
                    colorScheme="teal"
                    size="md"
                    onClick={() => handleRemove(idx)}
                    leftIcon={<AiOutlineDelete />}
                  >
                    Remove
                  </Button>
                </FormControl>
              </Stack>
            );
          })}
        </Stack>
      </CardBody>
      <CardFooter>
        <ButtonGroup w={'100%'} justifyContent="end">
          <Button colorScheme="teal" size="md" onClick={prevClicked}>
            Previous
          </Button>
          <Button
            colorScheme="teal"
            isLoading={inProgress}
            loadingText="Submitting..."
            disabled={inProgress || actionStatus}
            size="md"
            onClick={addAboutServiceDetails}
            whiteSpace="normal"
          >
            {isEdit ? 'Update' : 'Add'}
          </Button>
          <Button
            colorScheme="teal"
            size="md"
            style={{ display: displayNext ? 'inherit' : 'none' }}
            onClick={nextClicked}
          >
            Next
          </Button>
        </ButtonGroup>
      </CardFooter>
    </Card>
  );
}

export default AboutService;
